<script lang="ts" context="module">
  export type GameInitSettings = {
    prompts: string[];
    duration: number;
  };

  export type FinishGameCallback = (result: {
    correct: string[];
    incorrect: string[];
  }) => void;
</script>

<script lang="ts">
  import { onDestroy } from "svelte";

  export let gameInitSettings: GameInitSettings;
  export let finishGameCallback: FinishGameCallback;
  import { shuffle } from "./utils";

  const animationDuration = 300;

  const shuffled = shuffle(gameInitSettings.prompts);
  let pending = shuffled;
  let correct: string[] = [];
  let incorrect: string[] = [];
  $: score = correct.length;
  const start = new Date();
  const end = new Date(
    start.getTime() + 1000 * gameInitSettings.duration * 1.2
  );

  let current = pending.shift() ?? "error";

  let remaining = "";
  setTimer();
  function setTimer() {
    const rawRemaining = end.getTime() - new Date().getTime();
    remaining = (rawRemaining / 1000).toFixed(2);
    if (rawRemaining > 0) {
      window.requestAnimationFrame(() => {
        setTimer();
      });
    } else {
      finishGame();
    }
  }

  function finishGame() {
    finishGameCallback({ correct: [...correct], incorrect: [...incorrect] });
  }

  let lastCorrect: null | boolean = false;
  function accept(answer: boolean) {
    return function () {
      animateResult(answer);
      if (answer) {
        correct = [...correct, current];
      } else {
        incorrect = [...incorrect, current];
      }
      if (pending.length === 0) {
        finishGame();
      } else {
        current = pending.shift() ?? "error";
      }
    };
  }

  const right = accept(true);
  const wrong = accept(false);

  function animateResult(result: boolean) {
    lastCorrect = result;
    window.setTimeout(() => {
      lastCorrect = null;
    }, animationDuration);
  }

  let needsTiltReset = true;
  let accDebug = "";
  const motionEvt: Parameters<Window["addEventListener"]> = [
    "devicemotion",
    (event: Event) => {
      if (
        !(event instanceof DeviceMotionEvent) ||
        !event.accelerationIncludingGravity
      )
        return;
      const { z } = event.accelerationIncludingGravity;
      
      if (z == null) return;
      
      accDebug = z.toFixed(2);
      if (needsTiltReset) {
        if (Math.abs(z) < 4) {
          needsTiltReset = false;
        }
        return;
      }

      if (z < -8) {
        console.log("Up");
        right();
        needsTiltReset = true;
      } else if (z > 8) {
        console.log("Down");
        wrong();
        needsTiltReset = true;
      }
    },
  ];

  function initMotionEvents() {
    console.log("adding event listener");
    // Listen to DeviceMotionEvents
    window.addEventListener(...motionEvt);
  }
  initMotionEvents();

  onDestroy(() => {
    window.removeEventListener(...motionEvt);
  });
</script>

<div
  class="board"
  class:correct={lastCorrect === true}
  class:wrong={lastCorrect === false}
  style="--animation-duration: {animationDuration}ms"
>
  <div class="head">
    <span class="score">Score: {score} - remaining {remaining}</span>
    <button class="cancel" on:click={finishGame}>Cancel Game</button>
  </div>

  <div class="stack">
    <div class="stack-card">
      <span>{current}</span>
      <p>{accDebug}</p>
      <div class="action">
        <button on:click={right}>Yes</button>
        <button on:click={wrong}>No</button>
      </div>
    </div>
  </div>
</div>

<style>
  .board {
    display: flex;
    flex-flow: column nowrap;
    height: 100%;
    animation: var(--animation-duration) ease-in-out backwards;
    --correct: #93ec54;
    --wrong: #ff8335;
  }

  .board.correct {
    animation-name: correct;
  }
  .board.wrong {
    animation-name: wrong;
  }
  .head {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
  }
  .score {
    grid-column: 2 / 3;
    font-size: large;
  }

  .cancel {
    grid-column: 3 / 4;
    place-self: center end;
    border: 0;
    background: 0;
  }
  .stack {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .stack-card {
    margin: auto;
    width: 80%;
    background: inherit;
    position: absolute;
    inset: 0;
    display: grid;
    grid-template-rows: 1fr auto;
  }

  .stack-card span {
    font-size: 550%;
    word-wrap: break-word;
    line-height: 0.9;
    place-self: center;
  }

  .stack-card button {
    width: 200px;
    font-size: larger;
  }

  @keyframes correct {
    from {
      background-color: var(--correct);
    }
    to {
      background-color: unset;
    }
  }

  @keyframes wrong {
    from {
      background-color: var(--wrong);
    }
    to {
      background-color: unset;
    }
  }
</style>
